import React, { useState, useEffect } from "react";
import {
  Container,
  Box,
  Typography,
  Paper,
  Dialog,
  IconButton,
  CircularProgress,
} from "@mui/material";
import { useParams } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import { Close as CloseIcon } from "@mui/icons-material";
import Authenticated from "../components/Layouts/Authenticated";
import { createRecipeStyles } from "../components/Recipe/styles";
import RecipeCards from "../components/Recipe/RecipeCards";
import RecipeHeader from "../components/Recipe/RecipeHeader";
import RecipeImageAndDetails from "../components/Recipe/RecipeImageAndDetails";
import RecipeDescription from "../components/Recipe/RecipeDescription";
import RecipeComposition from "../components/Recipe/RecipeComposition";
import RecipeMaterialProperties from "../components/Recipe/RecipeMaterialProperties";
import RecipeMethod from "../components/Recipe/RecipeMethod";
import RecipeGallery from "../components/Recipe/RecipeGallery";
import { getStaticAssetPath } from "../utils/staticAssets";
import { useRecipes } from "../hooks/useRecipes";
import { PropertyGroup } from "../types/recipe";
import { CompositionIcon } from "../components/ui/RecipeIcons";
import { getFilteredProperties } from "../components/Recipe/utils";
import ErrorMessage from "../components/ui/ErrorMessage";

// NewRecipePage is the main page component for displaying a recipe.
// It handles fetching recipe data, loading/error states, and rendering all recipe sections.

const NewRecipePage: React.FC = () => {
  // Get the current theme for styling.
  const theme = useTheme();

  // Generate styles based on the theme.
  const styles = createRecipeStyles(theme);

  // Get the recipeId from the URL parameters.
  const { recipeId } = useParams<{ recipeId: string }>();

  // Custom hook to fetch recipe data and related info.
  const {
    recipe,
    author,
    fullContributors,
    linkedRecipes,
    loading,
    error,
    fetchRecipe,
  } = useRecipes();

  // State for controlling the gallery lightbox modal.
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState("");

  // Handler to open the lightbox with a selected image.
  const handleLightboxOpen = (image: string) => {
    setSelectedImage(getStaticAssetPath(image));
    setLightboxOpen(true);
  };

  // Fetch recipe data when recipeId changes.
  useEffect(() => {
    if (recipeId) {
      // Wrap fetchRecipe in a try/catch for extra safety.
      const fetch = async () => {
        try {
          await fetchRecipe(recipeId);
        } catch (err) {
          console.error("Failed to fetch recipe:", err);
        }
      };
      fetch();
    }
  }, [recipeId]);

  // Show loading spinner while fetching data.
  if (loading) {
    return (
      <Authenticated>
        <Container
          maxWidth="xl"
          sx={{
            mt: 4,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            py: 8,
            gap: 2,
          }}
        >
          <CircularProgress />
          <Typography variant="h6">Loading recipe...</Typography>
        </Container>
      </Authenticated>
    );
  }

  // Show error message if recipe is not found or fetch fails.
  if (error || !recipe) {
    return (
      <Authenticated>
        <Container maxWidth="xl" sx={{ mt: 4, py: 8 }}>
          <ErrorMessage message={error || "Recipe not found."} />
        </Container>
      </Authenticated>
    );
  }

  // Prepare property groups for material properties section.
  const allPropertyGroups: PropertyGroup[] = [
    recipe.data.physicalProperties,
    recipe.data.mechanicalTensionProperties,
    recipe.data.mechanicalCompressionProperties,
    recipe.data.chemicalProperties,
    recipe.data.opticalProperties,
    recipe.data.thermalProperties,
    recipe.data.barrierProperties,
  ];

  // Filter out empty property groups.
  const filteredProperties = getFilteredProperties(allPropertyGroups);

  // Filter linked recipes to only show published ones

  const publishedLinkedRecipes = linkedRecipes.filter(
    (r) => r.status === "PUBLISHED"
  );

  // Render the main recipe page layout and all sections.
  return (
    <Authenticated>
      <title>Recipe - Materiom Commons</title>
      <Container
        maxWidth="xl"
        sx={{
          ...styles.page.container,
          overflowX: "hidden",
          "& *": { minWidth: 0 },
          "& img, & video": { maxWidth: "100%", height: "auto" },
        }}
      >
        {/* Main recipe card with header, image/details, and description */}
        <Paper
          elevation={0}
          sx={{
            ...styles.page.mainCard,
            p: { xs: 2, sm: 3, md: 4 },
          }}
        >
          <RecipeHeader recipe={recipe} />

          {/* Image and Details */}
          <Box
            sx={{
              mb: 3,
              display: "flow-root",
              position: "relative",
              isolation: "isolate",
            }}
          >
            <RecipeImageAndDetails
              recipe={recipe}
              author={author}
              fullContributors={fullContributors}
            />
          </Box>

          {/* Description */}
          <Box sx={{ mb: 3 }}>
            <RecipeDescription recipe={recipe} />
          </Box>
        </Paper>

        {/* Composition and Material Properties sections using CSS Grid */}
        <Box
          sx={{
            mt: 3,
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
            gap: 3,
            alignItems: "stretch",
            clear: "both",
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <RecipeComposition recipe={recipe} />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <RecipeMaterialProperties filteredProperties={filteredProperties} />
          </Box>
        </Box>

        {/* Method section */}
        <Box sx={{ mt: 3 }}>
          <RecipeMethod recipe={recipe} />
        </Box>

        {/* Linked Recipes section, shown only if there are published linked recipes */}
        {publishedLinkedRecipes.length > 0 && (
          <Paper
            elevation={0}
            sx={{
              ...styles.page.sectionCard,
              mt: 3,
              p: { xs: 2, sm: 3 },
            }}
          >
            <Box sx={{ ...styles.page.sectionHeader, flexWrap: "wrap" }}>
              <CompositionIcon sx={{ fontSize: 36, mr: 1 }} />
              <Typography
                variant="h5"
                sx={{
                  ...styles.page.sectionTitle,
                  ml: 2,
                  mt: { xs: 1, sm: 0 },
                }}
              >
                Linked Recipes
              </Typography>
            </Box>
            <Box sx={{ width: "100%" }}>
              <RecipeCards recipes={publishedLinkedRecipes} editable={false} />
            </Box>
          </Paper>
        )}

        {/* Gallery section */}
        <Box sx={{ mt: 3 }}>
          <RecipeGallery recipe={recipe} onImageClick={handleLightboxOpen} />
        </Box>

        {/* Lightbox close button (floating) */}
        {lightboxOpen && (
          <Box
            sx={{
              position: "fixed",
              top: 24,
              right: 24,
              zIndex: 1301, // above MUI modal
              background: "#fff",
              borderRadius: "8px",
              boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
              width: 32,
              height: 32,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <IconButton
              onClick={() => setLightboxOpen(false)}
              sx={{
                p: 0,
                color: "#111",
                fontSize: 22,
                width: 32,
                height: 32,
              }}
              aria-label="Close image modal"
            >
              <CloseIcon fontSize="inherit" />
            </IconButton>
          </Box>
        )}

        {/* Lightbox dialog for viewing selected gallery image */}
        <Dialog
          open={lightboxOpen}
          onClose={() => setLightboxOpen(false)}
          maxWidth="lg"
          fullWidth
        >
          <Box
            component="img"
            src={selectedImage}
            alt="Gallery"
            sx={{
              width: "100%",
              height: "auto",
              maxHeight: "80vh",
              display: "block",
              objectFit: "contain",
            }}
          />
        </Dialog>
      </Container>
    </Authenticated>
  );
};

export default NewRecipePage;
